/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package cmp.navigation.authenticatednavbar

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.NetworkMonitor

internal class AuthenticatedNavbarNavigationViewModel(
    networkMonitor: NetworkMonitor,
) : BaseViewModel<Unit, AuthenticatedNavBarEvent, AuthenticatedNavBarAction>(
    initialState = Unit,
) {

    val isOffline = networkMonitor.isOnline
        .map(Boolean::not)
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5_000),
            initialValue = false,
        )

    override fun handleAction(action: AuthenticatedNavBarAction) {
        when (action) {
            AuthenticatedNavBarAction.DashboardTabClick -> {
                sendEvent(AuthenticatedNavBarEvent.NavigateToDashboard)
            }
            AuthenticatedNavBarAction.PatientsTabClick -> {
                sendEvent(AuthenticatedNavBarEvent.NavigateToPatients)
            }
            AuthenticatedNavBarAction.SettingsTabClick -> {
                sendEvent(AuthenticatedNavBarEvent.NavigateToSettings)
            }
        }
    }
}

internal sealed class AuthenticatedNavBarAction {
    data object DashboardTabClick : AuthenticatedNavBarAction()
    data object PatientsTabClick : AuthenticatedNavBarAction()
    data object SettingsTabClick : AuthenticatedNavBarAction()
}

internal sealed class AuthenticatedNavBarEvent {

    abstract val tab: AuthenticatedNavBarTabItem

    data object NavigateToDashboard : AuthenticatedNavBarEvent() {
        override val tab: AuthenticatedNavBarTabItem = AuthenticatedNavBarTabItem.DashboardTab
    }

    data object NavigateToPatients : AuthenticatedNavBarEvent() {
        override val tab: AuthenticatedNavBarTabItem = AuthenticatedNavBarTabItem.PatientsTab
    }

    data object NavigateToSettings : AuthenticatedNavBarEvent() {
        override val tab: AuthenticatedNavBarTabItem = AuthenticatedNavBarTabItem.SettingsTab
    }
}
